<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue';
import api from '@/api';
import { MAX_ORGANIZATION_TAG_NAME_LENGTH } from '@/constants';
import { DgrDialog } from '@stockmarkteam/donguri-ui';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import { OrganizationTag, OrganizationTagList } from '@/types';
import { organizationTagValidate } from '@/utils/validators';

const props = defineProps<{
  organizationTag?: OrganizationTag;
  organizationTagNames: OrganizationTagList;
}>();

const emit = defineEmits(['onClose', 'onSubmit']);

// このコンポーネントはDgrDialogをラップしているだけなので、openの値をこのコンポーネント内で変更しない。
const open = defineModel<boolean>('open', { default: false });

const { createSnackbar } = useSnackbar();

const organizationTagName = ref('');
const organizationTags = ref<OrganizationTagList>();

onMounted(() => {
  organizationTags.value = props.organizationTagNames;
  organizationTagName.value = props.organizationTag?.name || '';
});

watch(
  () => open.value,
  () => {
    if (open.value) {
      organizationTags.value = props.organizationTagNames;
      organizationTagName.value = props.organizationTag?.name || '';
    } else {
      emit('onClose');
    }
  },
);

const currentValidate = computed(() => {
  return organizationTagValidate(
    organizationTagName.value,
    props.organizationTagNames,
    props.organizationTag,
  );
});

const onSubmit = async () => {
  try {
    if (organizationTagName.value !== props.organizationTag?.name) {
      const targetOrgTag = organizationTags.value?.find(
        organizationTag => organizationTag.id === props.organizationTag?.id,
      );
      if (!targetOrgTag) throw new Error('targetOrgTag is undefined');
      await api.bulkOperateOrganizationTags({
        updateTags: [
          {
            id: targetOrgTag.id,
            name: organizationTagName.value,
          },
        ],
      });
    }
    emit('onSubmit');
    createSnackbar({
      message: '組織タグ名を変更しました',
      type: 'success',
    });
  } catch {
    createSnackbar({
      message: '組織タグ名の変更に失敗しました',
      type: 'error',
    });
  }
  close();
};
</script>

<template>
  <DgrDialog v-model:open="open" title="組織タグ名の編集">
    <template #description>
      <div class="modal-content">
        <p class="modal-input-label c-text-regular--m">組織タグ名</p>
        <input
          class="c-textInput"
          :class="{
            'c-formInput--error': currentValidate !== undefined,
          }"
          v-model.trim="organizationTagName"
          type="text"
          placeholder="組織タグ名を入力してください"
          data-testid="organization-tag-name-input"
        />
        <div class="modal-input-desc">
          <div class="modal-error-texts" v-if="currentValidate">
            <span data-testid="organization-tag-name-error-message">
              {{ currentValidate }}
            </span>
          </div>
          <div
            class="modal-text-counter"
            :class="{ 'modal-error-texts': currentValidate }"
          >
            <span data-testid="organization-tag-name-counter">
              {{
                organizationTagName.length +
                '/' +
                MAX_ORGANIZATION_TAG_NAME_LENGTH
              }}
            </span>
          </div>
        </div>
      </div>
    </template>

    <template #submit>
      <button
        type="button"
        class="o-create-button c-btn c-btn--auto c-btn--AnewsPrimary"
        :class="{ disabled: currentValidate !== undefined }"
        :disabled="currentValidate !== undefined"
        data-testid="submit-button"
        @click="onSubmit()"
      >
        保存
      </button>
    </template>
  </DgrDialog>
</template>
<style lang="scss" scoped>
input {
  outline: none;
}

.modal-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.modal-input-desc {
  display: flex;
  justify-content: flex-end;
  font-size: 10px;
  color: $color-gray600;

  .modal-text-counter {
    margin-left: 5px;
  }
  .modal-error-texts {
    color: $color-orange1000;
  }
}

.o-create-button.disabled {
  border: none;
}
</style>
